import * as SwitchPrimitive from "@radix-ui/react-switch";
import type { ReactNode } from "react";
import { useId } from "react";

import { cn } from "../lib/cn";
import { FOCUS_RING, HIT_SLOP, REDUCED_MOTION_SAFE } from "../lib/focus";

/**
 * The platform's toggle for a saved on/off setting.
 *
 * A `Switch` writes the moment it is flipped -- there is no separate save
 * step, and there is no uncontrolled mode, because a switch always mirrors
 * saved state rather than a form value waiting to be submitted. `Checkbox`
 * remains the right control for a form field that is submitted later.
 */
export type typeSwitchProps = {
  /** The row's visible label. Sentence case. */
  label: string;
  /** One supporting sentence under the label. */
  description?: string | undefined;
  /** Controlled. There is no uncontrolled mode -- a switch always mirrors saved state. */
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean | undefined;
  className?: string | undefined;
};

export const Switch = ({
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  className,
}: typeSwitchProps): ReactNode => {
  const switchId = useId();

  return (
    <div
      className={cn(
        "relative flex min-h-touch-target w-full items-center justify-between gap-3 py-2",
        className,
      )}
    >
      <div className="flex flex-col gap-1">
        <label htmlFor={switchId} className="text-body text-ink">
          {label}
        </label>
        {description !== undefined ? (
          <p className="text-caption text-ink-secondary">{description}</p>
        ) : null}
      </div>
      <SwitchPrimitive.Root
        id={switchId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className={cn(
          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-line transition-colors duration-100 ease-standard",
          REDUCED_MOTION_SAFE,
          FOCUS_RING,
          HIT_SLOP,
          "data-[state=checked]:border-primary data-[state=checked]:bg-primary-solid",
          "data-[state=unchecked]:bg-disabled-bg",
          "disabled:pointer-events-none disabled:border-disabled-border disabled:bg-disabled-bg",
        )}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            "pointer-events-none block size-5 translate-x-0.5 rounded-full bg-surface transition-transform duration-100 ease-standard",
            REDUCED_MOTION_SAFE,
            "data-[state=checked]:translate-x-5.5",
          )}
        />
      </SwitchPrimitive.Root>
    </div>
  );
};
